import { SetStateAction } from "react";
import { Card, CardHeader, Button, CardBody, Col, Row } from "reactstrap";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { ReactGlobals, TreeNode } from "../Types";
import backendService from "../../services/backend.service";
import { parseConeGeometry } from "./ThreeParsers";
import { argBooleanGUI, argNumberGUI } from "./ThreeGUIElements";

const ConeGeometryNode = (
  node: TreeNode,
  globals: ReactGlobals,
  load_node: (new_id: string, new_type: string) => void,
  handleTempContentChange: (event: {
    target: { value: SetStateAction<string> };
  }) => void
) => {
  let material = <meshStandardMaterial wireframe={true} />;

  let geometry =
    node.ID === globals.node.ID
      ? parseConeGeometry(globals.temporaryContent)
      : parseConeGeometry(node.Content);

  function save_geometry() {
    let json = {
      radius: geometry.props.args[0],
      height: geometry.props.args[1],
      radialSegments: geometry.props.args[2],
      heightSegments: geometry.props.args[3],
      openEnded: geometry.props.args[4],
      thetaStart: geometry.props.args[5],
      thetaLength: geometry.props.args[6],
    };
    const text = JSON.stringify(json);

    node.ID === globals.node.ID
      ? globals.setTemporaryContent(text)
      : backendService.change_content(node.ID, text).then((res) => {
          load_node(globals.node.ID, globals.node.Type);
        });
  }

  return (
    <Card key={Math.random().toString() + node.ID}>
      <CardHeader>
        <Button
          outline
          disabled={node.ID === globals.node.ID}
          onClick={() => {
            load_node(node.ID, node.Type);
          }}
        >
          {node.ID === globals.node.ID ? globals.temporaryName : node.Name}
        </Button>
      </CardHeader>
      <CardBody>
        <Row>
          <Col xs="auto">
            <div
              style={{
                width: "200px",
                height: "200px",
              }}
            >
              <Canvas camera={{ position: [0.0, 1.5, 1.5] }}>
                <ambientLight />
                <pointLight
                  position={[5.0, 5.0, 5.0]}
                  decay={0.1}
                  intensity={2.0}
                />
                <mesh>
                  {geometry}
                  {material}
                </mesh>
                <OrbitControls makeDefault />
              </Canvas>
            </div>
          </Col>
          <Col>
            {argNumberGUI(
              "radius",
              0.0,
              50.0,
              0.001,
              save_geometry,
              geometry.props.args,
              0,
              globals
            )}
            {argNumberGUI(
              "height",
              0.0,
              100.0,
              0.001,
              save_geometry,
              geometry.props.args,
              1,
              globals
            )}
            {argNumberGUI(
              "radialSegments",
              2,
              128,
              1,
              save_geometry,
              geometry.props.args,
              2,
              globals
            )}
            {argNumberGUI(
              "heightSegments",
              1,
              100,
              1,
              save_geometry,
              geometry.props.args,
              3,
              globals
            )}
            {argBooleanGUI(
              "openEnded",
              save_geometry,
              geometry.props.args,
              4,
              globals
            )}
            {argNumberGUI(
              "thetaStart",
              0.0,
              2.0 * Math.PI,
              0.001,
              save_geometry,
              geometry.props.args,
              5,
              globals
            )}
            {argNumberGUI(
              "thetaLength",
              0.0,
              2.0 * Math.PI,
              0.001,
              save_geometry,
              geometry.props.args,
              6,
              globals
            )}
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

export function add_ConeGeometry_to_dicts(
  constuctor_dict: Map<
    string,
    (
      node: TreeNode,
      globals: ReactGlobals,
      load_node: (new_id: string) => void,
      handleTempContentChange: (event: {
        target: { value: SetStateAction<string> };
      }) => void
    ) => JSX.Element
  >,
  type_prompt_dict: Map<string, string[]>,
  type_adding_dict: Map<string, string[]>,
  type_depth_dict: Map<string, number>
) {
  constuctor_dict.set("ConeGeometry", ConeGeometryNode);
  type_prompt_dict.set("ConeGeometry", []);
  type_adding_dict.set("ConeGeometry", []);
  type_depth_dict.set("ConeGeometry", 0);
}

export default ConeGeometryNode;
